import type { computeHoverHintFixedStyle } from "@/lib/viewportAnchoredPopover";

export type PortaledHoverHintComputedStyle = ReturnType<typeof computeHoverHintFixedStyle>;

export type PositionedPortaledHoverHintStyle = {
  revision: number;
  style: PortaledHoverHintComputedStyle;
};

/**
 * Pick the hover-hint panel position for the current anchor revision.
 * When the anchor cell changes while the panel stays open, ignore stale positioned
 * styles from the previous revision and use a fresh provisional measurement.
 */
export function resolvePortaledHoverHintStyle(args: {
  open: boolean;
  anchorRevision: number;
  positioned: PositionedPortaledHoverHintStyle | null;
  provisionalStyle: PortaledHoverHintComputedStyle | null;
}): PortaledHoverHintComputedStyle | null {
  if (!args.open) {
    return null;
  }
  if (args.positioned?.revision === args.anchorRevision) {
    return args.positioned.style;
  }
  return args.provisionalStyle;
}

/**
 * Merge a measured tooltip position for the active anchor revision.
 * Returns the previous positioned value when nothing moved (scroll jitter).
 */
export function mergeMeasuredPortaledHoverHintStyle(args: {
  anchorRevision: number;
  previous: PositionedPortaledHoverHintStyle | null;
  nextStyle: PortaledHoverHintComputedStyle;
}): PositionedPortaledHoverHintStyle {
  if (
    args.previous?.revision === args.anchorRevision &&
    args.previous.style.top === args.nextStyle.top &&
    args.previous.style.left === args.nextStyle.left &&
    args.previous.style.maxWidth === args.nextStyle.maxWidth &&
    args.previous.style.maxHeight === args.nextStyle.maxHeight &&
    args.previous.style.transform === args.nextStyle.transform
  ) {
    return args.previous;
  }
  return { revision: args.anchorRevision, style: args.nextStyle };
}
